/**
 * Vest NQ-PERP volume (2m bars from utt-engine `GET /api/vest-volume`) mapped
 * onto a chart's candles.
 *
 * Units: Vest NQ-PERP contracts, 1 = 1x Nasdaq-100 index notional; a CME NQ
 * future is $20 x the index, so NQ-equivalent = v * nqPerUnit (1/20).
 *
 * Re-aggregation follows the CME candles exactly (utils/candleSeries.ts):
 * a 2m bar opening at t belongs to the chart bar floor(t / I) * I. On a
 * timeframe shorter than 2m each candle shows the 2m bar that contains it
 * (the bar is drawn across its whole span, and the readout says "2m bar").
 *
 * The Vest series lives in the CME volume pane and holds exactly one point
 * per candle (whitespace where Vest has no bar), the same time points as the
 * CME volume histogram.
 */
import { displayVolume, formatDisplayVolume } from './vestFeed';
import type { Time } from 'lightweight-charts';

export const VEST_BAR_MS = 120_000;
export const DEFAULT_NQ_PER_UNIT = 1 / 20;

export type VestSource = 'tape' | 'kline' | 'mixed';

export interface VestBar {
  /** Bar open, ms UTC (epoch-aligned to 2m). */
  t: number;
  /** Total volume, Vest units. */
  v: number;
  /** Taker buy/sell volume; null when any minute of the bar is kline-derived. */
  buy: number | null;
  sell: number | null;
  delta: number | null;
  n: number | null;
  vwap: number | null;
  src: VestSource;
  closed: boolean;
  /**
   * Traded NQ-PERP prices (NOT translated to CME), from the tape only.
   * Absent from engines before the candle overlay; null on zero bars and on
   * kline / mixed bars (Vest kline prices are not the traded prints).
   */
  o?: number | null;
  h?: number | null;
  l?: number | null;
  c?: number | null;
}

export function isVestBar(b: unknown): b is VestBar {
  if (!b || typeof b !== 'object') return false;
  const x = b as Partial<VestBar>;
  const numOrNull = (v: unknown) => v === null || typeof v === 'number';
  const optPrice = (v: unknown) => v === undefined || numOrNull(v);
  return (
    typeof x.t === 'number' &&
    x.t % VEST_BAR_MS === 0 &&
    typeof x.v === 'number' &&
    numOrNull(x.buy) &&
    numOrNull(x.sell) &&
    optPrice(x.o) &&
    optPrice(x.h) &&
    optPrice(x.l) &&
    optPrice(x.c) &&
    (x.src === 'tape' || x.src === 'kline' || x.src === 'mixed')
  );
}

/** MNQ / NQ in any month with any suffix (MNQZ6.CME, NQH27, nq). */
export function isVestVolumeSymbol(symbol: string | null | undefined): boolean {
  if (!symbol) return false;
  const s = symbol.trim().replace(/^\//, '').toUpperCase().split('.')[0];
  const base = s.replace(/[FGHJKMNQUVXZ]\d{1,2}$/, '');
  return base === 'NQ' || base === 'MNQ';
}

/** Upsert by bar open time; returns a new map only when something changed. */
export function mergeVestBars(existing: ReadonlyMap<number, VestBar>, incoming: unknown[]): Map<number, VestBar> | null {
  let next: Map<number, VestBar> | null = null;
  for (const b of incoming) {
    if (!isVestBar(b)) continue;
    const cur = (next ?? existing).get(b.t);
    if (cur && sameBar(cur, b)) continue;
    if (!next) next = new Map(existing);
    next.set(b.t, { ...b, delta: b.delta ?? (b.buy !== null && b.sell !== null ? b.buy - b.sell : null) });
  }
  return next;
}

function sameBar(a: VestBar, b: VestBar): boolean {
  return (
    a.v === b.v &&
    a.buy === b.buy &&
    a.sell === b.sell &&
    a.src === b.src &&
    a.closed === b.closed &&
    a.n === b.n &&
    (a.o ?? null) === (b.o ?? null) &&
    (a.h ?? null) === (b.h ?? null) &&
    (a.l ?? null) === (b.l ?? null) &&
    (a.c ?? null) === (b.c ?? null)
  );
}

/** One chart bar's worth of Vest volume. */
export interface VestPoint {
  /** Chart bar open, seconds. */
  time: number;
  v: number;
  buy: number | null;
  sell: number | null;
  delta: number | null;
  n: number | null;
  vwap: number | null;
  src: VestSource;
  /** How many 2m bars were summed. */
  bars: number;
  /** The chart timeframe is shorter than the 2m bar shown. */
  spansCandles: boolean;
}

/**
 * Sum 2m bars into the chart's bars (seconds-keyed). Buy/sell/delta/n are
 * kept only if every summed bar has them; VWAP is volume-weighted over the
 * bars that have one.
 */
export function aggregateVestBars(bars: Iterable<VestBar>, timeframeMinutes: number): Map<number, VestPoint> {
  const intervalMs = Math.max(1, Math.round(timeframeMinutes * 60_000));
  const spans = intervalMs < VEST_BAR_MS;
  const out = new Map<number, VestPoint>();
  const pv = new Map<number, { pv: number; v: number }>();
  for (const b of bars) {
    const key = spans ? b.t : Math.floor(b.t / intervalMs) * intervalMs;
    const time = Math.floor(key / 1000);
    const cur = out.get(time);
    const split = b.src === 'tape' && b.buy !== null && b.sell !== null;
    if (!cur) {
      out.set(time, {
        time,
        v: b.v,
        buy: split ? b.buy : null,
        sell: split ? b.sell : null,
        delta: split ? (b.buy as number) - (b.sell as number) : null,
        n: split ? b.n : null,
        vwap: null,
        src: b.src,
        bars: 1,
        spansCandles: spans,
      });
    } else {
      const both = cur.buy !== null && split;
      cur.v += b.v;
      cur.buy = both ? (cur.buy as number) + (b.buy as number) : null;
      cur.sell = both ? (cur.sell as number) + (b.sell as number) : null;
      cur.delta = both ? (cur.buy as number) - (cur.sell as number) : null;
      cur.n = both && cur.n !== null && b.n !== null ? cur.n + b.n : null;
      cur.src = cur.src === b.src ? cur.src : 'mixed';
      cur.bars += 1;
    }
    if (b.vwap !== null && b.v > 0) {
      const acc = pv.get(time) ?? { pv: 0, v: 0 };
      acc.pv += b.vwap * b.v;
      acc.v += b.v;
      pv.set(time, acc);
    }
  }
  for (const [time, acc] of pv) {
    const p = out.get(time);
    if (p && acc.v > 0) p.vwap = acc.pv / acc.v;
  }
  return out;
}

/** The point shown on a candle opening at `candleTimeSec`. */
export function pointForCandle(points: ReadonlyMap<number, VestPoint>, candleTimeSec: number, timeframeMinutes: number): VestPoint | undefined {
  if (timeframeMinutes * 60_000 < VEST_BAR_MS) {
    const barOpen = Math.floor((candleTimeSec * 1000) / VEST_BAR_MS) * VEST_BAR_MS;
    return points.get(barOpen / 1000);
  }
  return points.get(candleTimeSec);
}

// ------------------------------------------------------------ merged pane

/**
 * Vest volume is drawn INSIDE the CME volume pane, as thin amber bars
 * centred in each CME bar (components/Chart/VestVolumeSeries.ts).
 *
 * - independent (default): own invisible price scale, so the bars are
 *   scaled to Vest's own visible range (CME volume is orders of magnitude
 *   larger; on one scale Vest would be invisible).
 * - same: stacked on top of the CME bar on the CME scale, in chart-contract
 *   equivalents (see vestContractEquivalent).
 */
export type VestScaleMode = 'independent' | 'same';

/** CME point values: MNQ $2, NQ $20 per index point. */
export function chartPointValue(symbol: string | null | undefined): number | null {
  if (!isVestVolumeSymbol(symbol)) return null;
  const s = (symbol as string).trim().replace(/^\//, '').toUpperCase().split('.')[0];
  return s.startsWith('MNQ') ? 2 : 20;
}

export function chartContractName(symbol: string | null | undefined): string {
  return chartPointValue(symbol) === 2 ? 'MNQ' : 'NQ';
}

/** Vest notional, USD: units x the bar's VWAP (else the chart price). */
export function vestNotional(p: VestPoint, chartPrice: number): number {
  return p.v * (p.vwap ?? chartPrice);
}

/** Vest volume in chart contracts: notional / (point value x chart price). */
export function vestContractEquivalent(p: VestPoint, pointValue: number, chartPrice: number): number {
  if (!(pointValue > 0) || !(chartPrice > 0)) return 0;
  return vestNotional(p, chartPrice) / (pointValue * chartPrice);
}

/** Vest's share of the combined (CME + Vest) notional, 0..1. */
export function vestShare(p: VestPoint, cmeVolume: number, pointValue: number, chartPrice: number): number | null {
  const v = vestNotional(p, chartPrice);
  const c = cmeVolume * pointValue * chartPrice;
  return v + c > 0 ? v / (v + c) : null;
}

/** Amber family, readable on dark and light; net-sell bars are also hatched. */
export interface VestBarColors {
  buy: string;
  sell: string;
  neutral: string;
}

export const DEFAULT_VEST_BAR_COLORS: VestBarColors = {
  buy: '#fbbf24', // amber-400: taker buys > sells
  sell: '#b45309', // amber-700 + hatch: sells > buys
  neutral: '#d97706', // amber-600 outline: kline-derived, no split
};

export type VestDelta = 'buy' | 'sell' | 'none';

export function deltaKind(p: VestPoint): VestDelta {
  if (p.delta === null) return 'none';
  return p.delta < 0 ? 'sell' : 'buy';
}

/** One entry of the custom series: a bar from `base` to `top`. */
export interface VestBarDatum {
  time: Time;
  base: number;
  top: number;
  kind: VestDelta;
}

export type VestBarPoint = VestBarDatum | { time: Time };

export interface ChartCandle {
  time: number;
  close: number;
  volume?: number;
}

/**
 * Exactly one entry per candle (whitespace where Vest has no bar), so the
 * series shares the CME volume series' time points.
 */
export function vestBarData(
  candles: readonly ChartCandle[],
  points: ReadonlyMap<number, VestPoint>,
  timeframeMinutes: number,
  mode: VestScaleMode,
  pointValue: number,
): VestBarPoint[] {
  return candles.map((c) => {
    const p = pointForCandle(points, c.time, timeframeMinutes);
    if (!p) return { time: c.time as Time };
    if (mode === 'same') {
      const base = c.volume || 0;
      return { time: c.time as Time, base, top: base + vestContractEquivalent(p, pointValue, c.close), kind: deltaKind(p) };
    }
    return { time: c.time as Time, base: 0, top: p.v, kind: deltaKind(p) };
  });
}

const sameDatum = (a: VestBarPoint | undefined, b: VestBarPoint): boolean => {
  if (!a || a.time !== b.time) return false;
  const av = a as Partial<VestBarDatum>;
  const bv = b as Partial<VestBarDatum>;
  return av.base === bv.base && av.top === bv.top && av.kind === bv.kind;
};

/** Indices of `next` to push with series.update(), or null when setData is needed. */
export function tailUpdates(prev: readonly VestBarPoint[], next: readonly VestBarPoint[], maxTail = 4): number[] | null {
  if (prev.length === 0 || next.length < prev.length || next.length > prev.length + 1) return null;
  if (prev[0].time !== next[0].time) return null;
  const changed: number[] = [];
  for (let i = next.length - 1; i >= 0; i--) {
    if (sameDatum(prev[i], next[i])) {
      if (i < next.length - maxTail) break;
      continue;
    }
    if (i < next.length - maxTail) return null;
    // lightweight-charts can't turn a bar back into whitespace via update().
    if (!('top' in next[i])) return null;
    changed.push(i);
  }
  return changed.reverse();
}

const compact = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (a >= 10_000) return `${(v / 1_000).toFixed(1)}k`;
  if (a >= 100) return v.toFixed(0);
  return v.toFixed(2);
};

export interface LegendInput {
  symbol: string;
  cmeVolume: number | null;
  chartPrice: number | null;
  point: VestPoint | undefined;
  /** Shown only while the Vest bars are on. */
  vestOn: boolean;
}

/** The volume pane legend: CME line, then the Vest lines. */
export function volumeLegend({ symbol, cmeVolume, chartPrice, point, vestOn }: LegendInput): string[] {
  const contract = chartContractName(symbol);
  // Vest chart feed volumes are stored scaled (qty * 10^sizeDecimals): shown in Vest units.
  const vol = cmeVolume === null ? '-' : formatDisplayVolume(symbol, displayVolume(symbol, cmeVolume));
  const lines = [isVestVolumeSymbol(symbol) ? `CME vol ${vol} ${contract}` : `VOL ${vol}`];
  if (!vestOn) return lines;
  const pv = chartPointValue(symbol);
  if (!point) {
    lines.push('Vest NQ-PERP: no bar');
    return lines;
  }
  const eq = pv && chartPrice ? vestContractEquivalent(point, pv, chartPrice) : null;
  let vest = `Vest ${compact(point.v)} units`;
  if (eq !== null) vest += ` = ${compact(eq)} ${contract}-eq ($${pv}/pt)`;
  lines.push(vest);
  const parts: string[] = [];
  if (point.buy !== null && point.sell !== null && point.delta !== null) {
    parts.push(`buy ${compact(point.buy)}`, `sell ${compact(point.sell)}`, `delta ${point.delta >= 0 ? '+' : ''}${compact(point.delta)}`);
  } else {
    parts.push(point.src === 'kline' ? 'kline-derived, no split' : 'partly kline-derived, no split');
  }
  const share = pv && chartPrice && cmeVolume !== null ? vestShare(point, cmeVolume, pv, chartPrice) : null;
  if (share !== null) parts.push(`${(share * 100).toFixed(share < 0.01 ? 2 : 1)}% of notional`);
  if (point.spansCandles) parts.push('2m bar');
  lines.push(parts.join(' · '));
  return lines;
}
